import { useState, useEffect } from "react";
import { EnrollmentSection } from "./EnrollmentSection";
import { Module, Lesson, FAQItem } from "@/lib/types";
import { StripePrice } from "@/types";
import AuthModal from "@/components/auth/AuthModal";
import { CourseContentList } from "./CourseContentList";
import { FAQ } from "./FAQ";

interface CourseDescriptionProps {
  content: string;
  course: {
    is_free: boolean;
    slug?: string;
    title?: string;
    description?: string;
    default_price?: StripePrice | null;
    faqData?: FAQItem[] | null;
  };
  modules: Module[];
  lessons: Record<string, Lesson[]>;
  hasAccess: boolean;
  setHasAccess: (value: boolean) => void;
  userId: string | undefined;
  lang: string;
}

// Component that renders HTML with placeholder replacements
const RenderHtmlWithPlaceholders = ({
  htmlContent,
  renderEnrollment,
  renderContentList,
  renderFAQ
}: {
  htmlContent: string;
  renderEnrollment: JSX.Element;
  renderContentList: JSX.Element;
  renderFAQ?: JSX.Element;
}) => {
  const [processedContent, setProcessedContent] = useState<JSX.Element[]>([]);

  useEffect(() => {
    if (!htmlContent) {
      setProcessedContent([]);
      return;
    }

    // Create a temporary div to parse the HTML content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // Find placeholder elements
    const enrollmentPlaceholder = tempDiv.querySelector('#enrollment-placeholder');
    const contentListPlaceholder = tempDiv.querySelector('#content-list-placeholder');
    const faqPlaceholder = tempDiv.querySelector('.faq-component-placeholder');
    
    if (enrollmentPlaceholder) {
      // Mark the enrollment placeholder for later replacement
      enrollmentPlaceholder.setAttribute('data-placeholder', 'enrollment');
    }
    
    
    if (contentListPlaceholder) {
      // Mark the content-list placeholder for later replacement
      contentListPlaceholder.setAttribute('data-placeholder', 'content-list');
    }
    
    if (faqPlaceholder) {
      // Mark the FAQ placeholder for later replacement
      faqPlaceholder.setAttribute('data-placeholder', 'faq-accordion');
    }
    
    // If no placeholders are found, render the entire content with prose styling
    if (!enrollmentPlaceholder && !contentListPlaceholder && !faqPlaceholder) {
      setProcessedContent([
        <div key="content-full" className="prose dark:prose-invert max-w-4xl mx-auto [&_h1]:text-center [&_h2]:text-center [&_h3]:text-center [&_h4]:text-center [&_h5]:text-center [&_h6]:text-center [&_p]:text-center [&_blockquote]:text-center [&_div]:text-center [&_span]:text-center [&_ul]:list-none [&_ol]:list-none [&_li]:pl-0" dangerouslySetInnerHTML={{ __html: htmlContent }} />
      ]);
      return;
    }
    
    // Convert the HTML structure into an array of elements and placeholders
    const elements: JSX.Element[] = [];
    
    // Use a more comprehensive approach to find and replace placeholders
    const remainingContent = tempDiv.innerHTML;

    // Find all placeholder divs and their positions
    const placeholderRegex = /<div[^>]*id="(enrollment|content-list|single-page-content)-placeholder"[^>]*><\/div>/g;
    const matches: Array<{fullMatch: string, type: string, startIndex: number, endIndex: number}> = [];
    let match: RegExpExecArray | null;

    while ((match = placeholderRegex.exec(remainingContent)) !== null) {
      matches.push({
        fullMatch: match[0],
        type: match[1],
        startIndex: match.index,
        endIndex: match.index + match[0].length
      });
    }

    // Also look for FAQ placeholder (which uses class instead of id)
    const faqRegex = /<div[^>]*class="[^"]*faq-component-placeholder[^"]*"[^>]*><\/div>/g;
    while ((match = faqRegex.exec(remainingContent)) !== null) {
      matches.push({
        fullMatch: match[0],
        type: 'faq-accordion',
        startIndex: match.index,
        endIndex: match.index + match[0].length
      });
    }
    
    // If no placeholders found, render entire content as one block
    if (matches.length === 0) {
      if (remainingContent.trim()) {
        elements.push(
          <div 
            key="content-0" 
            className="prose dark:prose-invert max-w-4xl mx-auto [&_h1]:text-center [&_h2]:text-center [&_h3]:text-center [&_h4]:text-center [&_h5]:text-center [&_h6]:text-center [&_p]:text-center [&_blockquote]:text-center [&_div]:text-center [&_span]:text-center [&_ul]:list-none [&_ol]:list-none [&_li]:pl-0" 
            dangerouslySetInnerHTML={{ __html: remainingContent }} 
          />
        );
      }
    } else {
      // Process content with placeholders
      let currentIndex = 0;
      
      matches.forEach((placeholderMatch, index) => {
        // Add content before this placeholder
        const beforeContent = remainingContent.slice(currentIndex, placeholderMatch.startIndex);
        if (beforeContent.trim()) {
          elements.push(
            <div 
              key={`content-${index}`} 
              className="prose dark:prose-invert max-w-4xl mx-auto [&_h1]:text-center [&_h2]:text-center [&_h3]:text-center [&_h4]:text-center [&_h5]:text-center [&_h6]:text-center [&_p]:text-center [&_blockquote]:text-center [&_div]:text-center [&_span]:text-center [&_ul]:list-none [&_ol]:list-none [&_li]:pl-0" 
              dangerouslySetInnerHTML={{ __html: beforeContent }} 
            />
          );
        }
        
        // Add the appropriate component for this placeholder
        if (placeholderMatch.type === 'enrollment') {
          elements.push(<div key={`enrollment-component-${index}`}>{renderEnrollment}</div>);
        } else if (placeholderMatch.type === 'content-list') {
          elements.push(<div key={`content-list-component-${index}`}>{renderContentList}</div>);
        } else if (placeholderMatch.type === 'single-page-content') {
          // Handle single-page-content if needed
        } else if (placeholderMatch.type === 'faq-accordion') {
          elements.push(<div key={`faq-component-${index}`}>{renderFAQ}</div>);
        }
        
        currentIndex = placeholderMatch.endIndex;
      });
      
      // Add any remaining content after the last placeholder
      const afterContent = remainingContent.slice(currentIndex);
      if (afterContent.trim()) {
        elements.push(
          <div 
            key={`content-after`} 
            className="prose dark:prose-invert max-w-4xl mx-auto [&_h1]:text-center [&_h2]:text-center [&_h3]:text-center [&_h4]:text-center [&_h5]:text-center [&_h6]:text-center [&_p]:text-center [&_blockquote]:text-center [&_div]:text-center [&_span]:text-center [&_ul]:list-none [&_ol]:list-none [&_li]:pl-0" 
            dangerouslySetInnerHTML={{ __html: afterContent }} 
          />
        );
      }
    }
    
    setProcessedContent(elements);
  }, [htmlContent, renderEnrollment, renderContentList, renderFAQ]);

  return <>{processedContent}</>;
};

// Function to check if content has FAQ placeholder
const hasFAQPlaceholder = (htmlContent: string) => {
  return htmlContent && htmlContent.includes('faq-component-placeholder');
};

// Function to clean duplicate content - now disabled to allow markdown titles
const cleanDuplicateContent = (htmlContent: string, courseTitle?: string): string => {
  // Return content as-is - let markdown handle all title rendering
  return htmlContent;
};

export function CourseDescription({
  content,
  course,
  modules,
  lessons,
  hasAccess,
  setHasAccess,
  userId,
  lang
}: CourseDescriptionProps) {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  
  // Clean the content to remove duplicate title and description
  const cleanedContent = cleanDuplicateContent(content, course.title);
  
  // Check if content has enrollment placeholder (check in the processed HTML for the placeholder div)
  const hasEnrollmentPlaceholder = cleanedContent && cleanedContent.includes('enrollment-placeholder');
  
  // Show default header only if: no content OR content exists but no enrollment placeholder
  const shouldShowDefaultHeader = !cleanedContent || (cleanedContent && !hasEnrollmentPlaceholder);
  
  // Use FAQ data from course object if it exists and content has placeholder
  const shouldShowFAQ = hasFAQPlaceholder(cleanedContent) && course.faqData;
  
  // Debug: Log FAQ detection
  console.log('FAQ Debug:', {
    hasCleanedContent: !!cleanedContent,
    containsFAQ: cleanedContent?.includes('{{faq}}'),
    containsFAQPlaceholder: cleanedContent?.includes('faq-placeholder'),
    courseFAQData: course.faqData,
    shouldShowFAQ
  });
  
  // Prepare the enrollment component for placeholder replacement
  const enrollmentComponent = (
    <EnrollmentSection
      course={{ ...course, modules }}
      userId={userId}
      hasAccess={hasAccess}
      setHasAccess={setHasAccess}
      lang={lang}
      setAuthModalOpen={setIsAuthModalOpen}
    />
  );
  
  // Prepare the content list component for placeholder replacement
  const contentListComponent = (
    <CourseContentList
      modules={modules}
      lessons={lessons}
      hasAccess={hasAccess}
    />
  );
  
  // Prepare the FAQ component if FAQ data exists
  const faqComponent = shouldShowFAQ ? (
    <FAQ items={course.faqData!} />
  ) : undefined;

  return (
    <div className="max-w-4xl mx-auto py-8">
      {/* Single centered column */}
      {cleanedContent ? (
        <RenderHtmlWithPlaceholders
          htmlContent={cleanedContent}
          renderEnrollment={enrollmentComponent}
          renderContentList={contentListComponent}
          renderFAQ={faqComponent}
        />
      ) : shouldShowDefaultHeader ? (
        <div className="space-y-8">
          <div className="prose dark:prose-invert max-w-4xl mx-auto [&_h1]:text-center [&_h2]:text-center [&_h3]:text-center [&_h4]:text-center [&_h5]:text-center [&_h6]:text-center [&_p]:text-center [&_blockquote]:text-center [&_div]:text-center [&_span]:text-center [&_ul]:list-none [&_ol]:list-none [&_li]:pl-0">
            <h2>Course Overview</h2>
            <p>{course.description || 'No description available'}</p>
          </div>
          
          {/* Centered enrollment card */}
          {enrollmentComponent}
          
          {/* Centered content list */}
          <div className="prose dark:prose-invert max-w-4xl mx-auto [&_h1]:text-center [&_h2]:text-center [&_h3]:text-center [&_h4]:text-center [&_h5]:text-center [&_h6]:text-center [&_p]:text-center [&_blockquote]:text-center [&_div]:text-center [&_span]:text-center [&_ul]:list-none [&_ol]:list-none [&_li]:pl-0">
            {contentListComponent}
          </div>
        </div>
      ) : null}
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={isAuthModalOpen} 
        onOpenChange={setIsAuthModalOpen} 
        lang={lang}
        redirectCallback={() => {
          // This will be called after authentication
          // No auto-enrollment for free courses - user must click the enrollment button
        }}
      />
    </div>
  );
}
